import React from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { PRIMARY_PHONE } from '../lib/contact';

const ModernCTA = () => {
  return (
    <div className="relative bg-gradient-to-br from-gray-50 via-white to-yellow-50 py-16 md:py-24 overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-brand-navy/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-brand-yellow/40 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left side - Content */}
            <div className="p-8 md:p-12 lg:p-16">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-brand-navy mb-6">
                  Ready to Start Your
                  <span className="relative whitespace-nowrap after:absolute after:left-0 after:-bottom-1 after:h-1.5 after:w-full after:bg-brand-yellow after:-z-0 after:rounded-full"> Next Project?</span>
                </h2>
                <p className="text-gray-600 text-lg mb-8">
                  Let's discuss how we can bring your construction vision to life with our expertise and dedication to quality.
                </p>

                {/* Contact buttons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-10">
                  <Link to="/contact-us">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full sm:w-auto bg-brand-navy text-white hover:bg-brand-navy-light font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      Get Free Consultation
                    </motion.button>
                  </Link>
                  <motion.a
                    href={`tel:${PRIMARY_PHONE.tel}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full sm:w-auto bg-white border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-bold py-4 px-8 rounded-full shadow hover:shadow-lg transition-all duration-300 text-center"
                  >
                    Call Us Now
                  </motion.a>
                </div>

                {/* Quick contact info */}
                <div className="space-y-4">
                  <motion.a
                    href={`tel:${PRIMARY_PHONE.tel}`}
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-3 text-gray-700 hover:text-brand-navy transition-colors"
                  >
                    <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center">
                      <FaPhone className="text-brand-navy" />
                    </div>
                    <span className="font-medium">{PRIMARY_PHONE.display}</span>
                  </motion.a>
                  <motion.a
                    href="mailto:bidaver@gmail.com"
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-3 text-gray-700 hover:text-brand-navy transition-colors"
                  >
                    <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center">
                      <FaEnvelope className="text-brand-navy" />
                    </div>
                    <span className="font-medium">bidaver@gmail.com</span>
                  </motion.a>
                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-center gap-3 text-gray-700"
                  >
                    <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center flex-shrink-0">
                      <FaMapMarkerAlt className="text-brand-navy" />
                    </div>
                    <span className="font-medium">Kopar Khairane, Navi Mumbai 400710</span>
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Right side - Visual */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative bg-gradient-to-br from-brand-navy to-brand-navy-dark p-8 md:p-12 lg:p-16 flex items-center justify-center"
            >
              <div className="text-center text-white">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="text-8xl md:text-9xl font-bold mb-6 opacity-30 text-brand-yellow"
                >
                  26
                </motion.div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Years of Excellence
                </h3>
                <p className="text-blue-100 text-lg mb-8">
                  Serving Navi Mumbai and beyond with quality construction services
                </p>
                
                {/* Floating elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute top-10 right-10 w-20 h-20 bg-white/10 rounded-lg backdrop-blur-sm"
                />
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                  className="absolute bottom-10 left-10 w-24 h-24 bg-white/10 rounded-lg backdrop-blur-sm"
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ModernCTA;

